/**
 * Patient Access (PAC) intro-call script — the beat definitions a rep works live
 * during the 15-minute intro call.
 *
 * ENG-OWNED. This is a typed definition in the repo, not a FHIR Questionnaire:
 * wording changes go through a PR. Content is transcribed from the PAC portal's
 * Route 1 script via the Orbit mockup
 * (`everself-design-system/mockups/orbit-ci-intro-call-card.html`), which is the
 * source of truth for copy and widget vocabulary.
 *
 * SCRIPT LINES ARE STATIC. Nothing is interpolated into the words the rep says:
 * `[procedure]`, `[location]`, `[lb]`, `[time]`, `[provider name]` and
 * `[today's verified price]` render literally, so the team reads the same
 * sentence on every call. Only chart FIELDS are computed — the weight-loss
 * estimate, the physician flag and the slot list.
 *
 * NO FREE TEXT. Every input widget is a select, checkbox or chip. Answering the
 * call is what documents it.
 *
 * SHARED VOCABULARY. The option lists, the widget union and the spoken
 * explanation copy in this module back all four Patient Access scripts — the
 * scheduled intro call defined below, the PN consultation, the warm hand-off and
 * the PAC intro call. Only `buildCiIntroCallScript` is specific to the scheduled
 * card, and that card is legacy.
 */

import { Patient } from '@medplum/fhirtypes';
import { Track } from './constants';

/** Procedure short forms. Internal vocabulary — never spoken as an abbreviation. */
export const PROCEDURE_OPTIONS = ['ESG', 'ESG Max', 'IGB', 'SIS', 'TORe'] as const;
export type PacProcedure = (typeof PROCEDURE_OPTIONS)[number];

/**
 * Average total-body-weight loss per procedure. Backs the weight-loss FIELD
 * only — the percentage is never shown to the patient, only the pound figure
 * and its working.
 */
export const PROCEDURE_AVERAGE_LOSS: Record<PacProcedure, number> = {
  ESG: 0.2,
  'ESG Max': 0.235,
  IGB: 0.12,
  SIS: 0.15,
  TORe: 0.1,
};

export const GOAL_OPTIONS = [
  'Find a lasting way to lose weight',
  'Improve my health condition',
  'Find an alternative to GLP-1',
  'Learn more about ESG',
  'Lose weight I’ve regained after bariatric surgery',
  'Just explore my options',
] as const;

/** Collapsed-row labels for the goal, so a long sentence does not blow the line. */
export const GOAL_SHORT_LABELS: Record<string, string> = {
  'Find an alternative to GLP-1': 'Alternative to GLP-1s',
  'Just explore my options': 'Exploring options',
  'Lose weight I’ve regained after bariatric surgery': 'Regain after surgery',
  'Find a lasting way to lose weight': 'Something lasting',
  'Improve my health condition': 'A health condition',
  'Learn more about ESG': 'Learning about ESG',
};

export const PRIOR_PROCEDURE_OPTIONS = [
  'None',
  'Gastric sleeve',
  'Gastric bypass',
  'ESG with another provider',
  'Another procedure, including LAP-Band',
] as const;

export const GLP1_OPTIONS = ['Tried GLP-1s and stopped', 'Still on GLP-1s', 'Never tried GLP-1s'] as const;

export const PAYMENT_OPTIONS = [
  'Employer benefit',
  'Monthly financing',
  'Pay in full',
  'HSA or FSA',
  'Not sure yet',
] as const;

export const OUTCOME_OPTIONS = [
  'Booked',
  'To send a booking link',
  'Maybe in the future',
  'Not qualified',
  'Not interested',
] as const;
export type PacOutcome = (typeof OUTCOME_OPTIONS)[number];

/** Outcomes that read as a positive close in the collapsed preview. */
export const POSITIVE_OUTCOMES: readonly PacOutcome[] = ['Booked', 'To send a booking link'];

export const LIKELIHOOD_OPTIONS = ['High', 'Medium', 'Low', 'Unlikely'] as const;

export const BARRIER_OPTIONS = [
  'Pricing/financing',
  'Competitor',
  'Efficacy',
  'Safety/risks',
  'Procedure scheduling',
  'Credibility',
] as const;

/** Coarse timeline buckets for the procedure the lead is considering. */
export const PROCEDURE_DATE_OPTIONS = [
  'In the next few weeks',
  'In the next couple of months',
  'A few months from now',
  'No timeline yet',
] as const;

/**
 * Published physician per clinic. Eng-owned rather than read from FHIR
 * Location/PractitionerRole: "published physician" is a marketing and legal
 * fact, not a scheduling one, and this beat books nothing — it records the
 * location the lead is interested in.
 *
 * `null` means the clinic has no published physician and the card must raise a
 * do-not-name flag instead of naming one.
 *
 * **This map no longer defines the market list.** Its keys are placeholder names
 * that predate the canonical market registry (BAR-2597) and match almost none of
 * it, so the Location selects read `useMarketOptions()` instead. Until the
 * provider sheet lands, a market absent from this map raises the do-not-name flag
 * rather than rendering no physician guidance at all — see `physicianFor` in
 * `PacIntroCallCard`.
 */
export const CLINIC_PHYSICIANS: Record<string, string | null> = {
  'Greater Jacksonville': 'Dr. Reed Vandermeer',
  'Greater Tampa': 'Dr. Priya Anand',
  'Greater Atlanta': 'Dr. Marcus Ellery',
  'Greater Boston': null,
  'Greater Charlotte': null,
  'Temecula Valley': null,
};

/**
 * One bio line per published physician, for the doctor-bio beat. Keys match the
 * values of `CLINIC_PHYSICIANS`.
 *
 * Content is a placeholder until the provider sheet lands. A clinic with no
 * published physician has no entry, and therefore no bio to show — which is the
 * correct behaviour, not a gap.
 */
export const DOCTOR_BIO: Record<string, string[]> = {
  'Dr. Reed Vandermeer': ['Bio line pending — from the provider sheet'],
  'Dr. Priya Anand': ['Bio line pending — from the provider sheet'],
  'Dr. Marcus Ellery': ['Bio line pending — from the provider sheet'],
};

/* -------------------------------------------------------------------------
   Long-form script copy. Patient-facing register; internal short forms are
   never spoken aloud.
   ------------------------------------------------------------------------- */

const FRAME =
  'I’d love to understand what you’re hoping for and see how Everself might be able to help. I’ll answer any practical questions I can, and if a clinical consultation feels like a helpful next step, I can help you arrange one.';
/** Zoom opening only — an inbound caller has no camera to reassure them about. */
const FRAME_CAMERA = ' And just so you know, your camera is completely optional.';

const CARE =
  'Even with a much smaller stomach, it’s crucial that you eat and exercise well. So our services come with 12 months of personalized care. This includes a personal dietitian, a nurse practitioner on call 24/7, group support and a smart scale.';

const PRICING =
  'You don’t need to make any decisions today—not until you’ve had a chance to speak with one of our providers. I do want to help you navigate and understand the cost and the options available ahead of time, so there are no surprises. The complete Everself program for [procedure] is [today’s verified price]. That includes the procedure, comprehensive lab work, a smart scale and twelve months of care, including dietitian support, groups and 24/7 clinical support.';

const PAYMENT =
  'We offer financing, accept HSA and FSA funds, and partner with a small but growing group of large employers that may help cover the cost. I can help you check your financing options or see whether your employer may be one of our partners. Have you given any thought to which option might work best for you?';

const CONSULT =
  'I can help you schedule a free virtual consultation with one of our providers as soon as today or tomorrow. They’ll take a closer look at your health history, answer your medical questions, and explain how the procedure might work for you. There’s absolutely no pressure—it’s simply a chance to learn more and see whether it feels like the right fit. Would you be interested in setting that up?';

const EXPECT =
  '[Provider name] is wonderful. They’ll review your medical history, answer any medical questions you have, and help you understand whether the procedure may be a good fit for you. The consultation will take place over Zoom and usually lasts about 30 to 45 minutes. We’ll email you the Zoom link after this call.';

/**
 * Per-procedure explanation copy for the "Explain the procedure" beat.
 *
 * Canon: everself-design-system @ origin/main,
 * `mockups/orbit-ci-intro-call-card.html` → `EXPLAIN`.
 *
 * These name the procedure out loud, which is why they are per-procedure rather
 * than one line with a [procedure] token — see `sayByProcedure` on `PacWidget`.
 */
export const PROCEDURE_EXPLANATIONS: Record<string, { proc: string; dur: string }> = {
  ESG: {
    proc: 'ESG is a one-time endoscopic procedure done through the mouth, with no cutting. We shrink the size of your stomach by 70% — from the size of a football to a banana — so you feel full faster and for longer.',
    dur: 'The ESG takes about 60 minutes, and you’re home the same day. You’d be back to work in 2–3 days, so if you go on a Friday that means resting over the weekend.',
  },
  'ESG Max': {
    proc: 'ESG Max combines two ways of helping you feel less hungry, in one procedure done through the mouth, with no cutting. The first is the ESG itself — we make your stomach smaller, so you feel full sooner and stay full longer. The second is a gentle treatment of the stomach lining that calms the cells that make ghrelin, your main hunger hormone — so you feel hungry less often in the first place.',
    dur: 'ESG Max takes a little longer than a standard ESG, and you’re home the same day. You’d be back to work in 2–3 days, so if you go on a Friday that means resting over the weekend.',
  },
  IGB: {
    proc: 'The gastric balloon is a soft balloon we place through the mouth, with no cutting. It takes up space in your stomach so you feel full faster and for longer, and it comes out after six months.',
    dur: 'Placing the balloon takes about 30 minutes, and you’re home the same day. Most people feel back to normal within a few days, and removal at six months is another short visit.',
  },
  SIS: {
    proc: 'SIS brings your sleeve back down to size endoscopically, through the mouth — no cutting and no second surgery — so you feel full faster and for longer again.',
    dur: 'The SIS takes 30 to 45 minutes, and you’re home the same day. You’d be back to work in 2–3 days.',
  },
  TORe: {
    proc: 'TORe tightens the outlet from your bypass endoscopically, through the mouth — no cutting and no second surgery — so food moves through more slowly and you stay full for longer.',
    dur: 'The TORe takes about 45 minutes, and you’re home the same day. You’d be back to work in 2–3 days.',
  },
};

/* -------------------------------------------------------------------------
   Widget vocabulary
   ------------------------------------------------------------------------- */

/**
 * Ids of the fields the card computes rather than asks. `sitePhysicians` is the
 * physicians who actually have procedure blocks at the chosen location, read from
 * availability — not a hand-maintained list.
 */
export type PacComputedField =
  /**
   * The employer section's verdict (BAR-2918): ✓ "Employer benefit · Carrum" or
   * ✗ "No employer benefit", read from the answers the employer search stored.
   */
  | 'employerBenefit'
  | 'weightLossEstimate'
  | 'physicianFlag'
  | 'doctorBio'
  | 'bmi'
  | 'sitePhysicians'
  /**
   * Whether Cherry already knows this patient, shown beside the Send-link action
   * (BAR-2604). Cherry approves patients in a phase Orbit does not automate, so
   * without this a rep offers financing and finds out at charge time.
   */
  | 'cherryStatus'
  /**
   * The Cherry status and its Send-link action as one row (BAR-2923). The link
   * hides once Cherry has approved the patient: there is nothing left to apply for.
   */
  | 'cherryAccount'
  /**
   * The PN card v2.0's Cherry application row (BAR-3000): Send Cherry
   * application, then "Application link sent" with Resend. Shown once Cherry is
   * one of the payment types.
   */
  | 'cherryApplication';

/** Named side effects an action link can fire. */
export type PacAction =
  | 'sendCherryLink'
  | 'checkEmployerCoverage'
  | 'openPaymentOrder'
  /** Third-party financing partners, opened in a new tab (BAR-2923). */
  | 'openPatientFi'
  | 'openCareCredit';

/**
 * The financing partners' public sites (BAR-2923). Constants, not build vars:
 * they are the same in every environment and carry nothing secret. Here rather
 * than beside the action handler so the script can show the CareCredit link as
 * its own address, and the two cannot drift.
 */
export const PATIENTFI_URL = 'https://app.patientfi.com/v2/admin/practice/dashboard';
export const CARECREDIT_URL = 'https://barien.do/carecredit';

/**
 * Spoken copy that names the procedure out loud, keyed by procedure.
 *
 * The documented exception to the static-script rule: "ESG is a one-time
 * endoscopic procedure…" cannot be written with a [procedure] token, because the
 * whole sentence differs per procedure, not just the noun. Selecting the
 * procedure swaps the line; nothing is interpolated into it.
 */
interface ProcedureSpokenCopy {
  sayByProcedure?: Record<string, string>;
  /** Shown until a procedure is chosen. */
  sayFallback?: string;
}

export type PacWidget =
  | ({ kind: 'checkbox'; id: string; label: string; say?: string } & ProcedureSpokenCopy)
  /**
   * A script action with no control: a titled line the rep reads, with its
   * spoken copy underneath. The PN consultation is built from these — only what
   * is worth charting gets an input there, so a beat ticks off what it captured
   * rather than off "the rep says they read it". Deliberately absent from
   * INPUT_WIDGET_KINDS: it collects nothing.
   */
  | ({ kind: 'action'; label: string; say?: string } & ProcedureSpokenCopy)
  | {
      kind: 'select';
      id: string;
      label: string;
      /** Static options. Omitted when `optionsSource` supplies them at render time. */
      options?: readonly string[];
      /**
       * Resolved by the widget renderer, not by this module. The market list is
       * fetched from Medplum and this script is a static const, so it can declare
       * *which* list it wants but cannot call a hook to get it.
       *
       * Overlaps in spirit with `locationSelect` below, which arrived on main from
       * BAR-2590 for the same underlying reason. They differ in what they return:
       * this one yields a market *name* for the intro call's `loc`, that one also
       * yields a `Location` reference so procedure availability can be scoped to a
       * site. Worth collapsing into one mechanism — see the note on
       * `useMarketOptions`.
       */
      optionsSource?: 'markets';
      say?: string;
    }
  /**
   * A select over Orbit's procedure `Location`s, read live rather than listed
   * here. Records the location's name under `id` and its reference under
   * `${id}Reference`, so the card can scope availability to the chosen site.
   */
  | {
      kind: 'locationSelect';
      id: string;
      label: string;
      say?: string;
      /** The PN card v2.0 (BAR-3000): only the self-pay centres of `ELIGIBLE_LOCATIONS`. */
      eligibleOnly?: boolean;
    }
  /**
   * The employer path's Location on the PN card v2.0 (BAR-3000): the confirmed
   * benefit administrator's centres from `ELIGIBLE_LOCATIONS`, none before the
   * benefit is confirmed. Records the market name under `id`. Rendered by the
   * card, which knows the administrator.
   */
  | { kind: 'employerLocationSelect'; id: string; label: string; say?: string }
  /**
   * A result that points to another path offers it (BAR-2995): "Switch to
   * Insurance" or "Switch to Self-pay" moves the PN card v2.0's track chip.
   * The patient's track itself switches when the card is saved. Rendered by
   * the card, which owns the chip.
   */
  | { kind: 'switchTrackLink'; to: 'self-pay' | 'employer' | 'insurance' }
  /**
   * The employer typeahead (BAR-2918): searches bariendo-be's employer
   * directory through the `coverage-lookup` bot, the same one the signup flow
   * searches, and checks the chosen employer for a partner benefit. Records the
   * employer's name under `id` and the verdict under `resultId` — the
   * administrator, or `none` — so the saved note keeps what the call found.
   * Rendered by the card: the lookup needs the bot and the caller's role.
   */
  | { kind: 'employerSearch'; id: string; resultId: string; label: string; say?: string }
  /**
   * The coverage section's next step (BAR-2921): moves the patient to the
   * coverage track when they are not on it, and creates the verification task
   * for Care Coordinators. Records what it did under `id`, so the saved note
   * says so and the button is not offered twice. Rendered by the card: it needs
   * the patient's track, the caller's role and the bots.
   */
  | { kind: 'verificationTask'; id: string; coverage: 'employer' | 'insurance' }
  /**
   * The insurer select (BAR-2919): the insurers for the patient's state, read
   * from Medplum's insurer Organizations (seeded by bariendo-be, the list signup
   * offers). Records the name under `id`, the Organization reference under
   * `${id}Reference` and the payer key under `${id}Key`. Rendered by the card.
   */
  | {
      kind: 'insurerSelect';
      id: string;
      label: string;
      say?: string;
      /**
       * The PN card v2.0 (BAR-2998): a plain dropdown, no typeahead, ending in
       * "Mine isn't listed" as signup's picker does. Off, the shipped searchable select.
       */
      notListedOption?: boolean;
    }
  /**
   * The insurance section's likely-eligibility line (BAR-2919), computed by the
   * card from the BMI, conditions, source, insurer and network. Recorded under
   * `id` (the line) and `${id}Eligible` (`yes`/`no`), so the saved note keeps
   * what the call concluded and later rows can key off it.
   */
  | { kind: 'insuranceEligibility'; id: string; label: string }
  /** Single-select chips. */
  | { kind: 'chips'; id: string; label: string; options: readonly string[]; say?: string }
  | {
      kind: 'multiChips';
      id: string;
      label: string;
      options: readonly string[];
      say?: string;
      /**
       * What a chip reads, by option, where that differs from the value stored.
       * The PN card v2.0 shows the weight-related conditions by their common
       * names while the note keeps the clinical ones (BAR-2994).
       */
      optionLabels?: Readonly<Record<string, string>>;
    }
  /** A static spoken line with no input attached. */
  | { kind: 'script'; text: string }
  /** A spoken line gated on what the patient said, e.g. "If they say X —". */
  | { kind: 'conditionalScript'; condition: string; text: string }
  | { kind: 'warning'; text: string }
  /** A neutral coaching line for the rep — not spoken, not a warning. */
  | { kind: 'note'; text: string }
  /**
   * Free text. The one exception to the no-typing rule, for what no option list
   * can hold — the PN consultation's fun facts to remember the patient by. An
   * input kind: a filled note is captured work.
   */
  | {
      kind: 'text';
      id: string;
      label: string;
      /** The line read for it. Drawn only on the PN card v2.0, in its Script column. */
      say?: string;
    }
  | { kind: 'computed'; field: PacComputedField; label: string; say?: string }
  /**
   * A value the card already knows, shown read-only.
   *
   * The scheduled intro call carries the eligibility form's answers, so the goal
   * is a fact to read back rather than a question to ask. Deliberately absent
   * from INPUT_WIDGET_KINDS: it displays an answer, it does not collect one.
   */
  | { kind: 'prefilledValue'; id: string; label: string }
  | { kind: 'actionLink'; label: string; linkText: string; action: PacAction; say?: string }
  /** The live NP-consultation slot grid. */
  | { kind: 'slotGrid'; id: string }
  /**
   * The live procedure-date grid: days drawn from the physician's block schedule
   * at the patient's location, plus an "not ready to commit" option.
   *
   * Records the date the patient agreed to — it books nothing. The CC team
   * schedules once payment completes, which is why this is a day picker and not
   * the slot grid above.
   */
  | { kind: 'procedureDateGrid'; id: string }
  /**
   * One frequently-asked question, closed until the rep taps it open.
   *
   * The PAC intro call is the one script whose shape is set by the *caller*
   * rather than by the rep: they ring with a question, and the rep has to find
   * the answer while the caller waits. Laid out flat, ten answers with their
   * branches are a wall of script to read past; behind a title each is one tap.
   * Collects nothing — opening a panel to read it is not the same as the caller
   * having asked it.
   *
   * The shape mirrors the Patient Access FAQ doc rather than flattening it: one
   * question has a standard answer *and* the branches that actually come up, and
   * a rep who cannot see which branch they are in reads the wrong one.
   */
  | {
      kind: 'faq';
      question: string;
      /** How callers actually phrase it — what a rep scans to find the right panel. */
      asks?: readonly string[];
      /** The standard answer first, then the branches. `when` gates a branch. */
      responses: readonly { when?: string; say: string }[];
      /** Reference the rep reads off rather than says: exact prices, the centre list. */
      facts?: readonly string[];
      /** What must never be said on this question. Rendered as the amber warning. */
      never?: string;
    }
  /**
   * A fact the card already knows before the call, shown read-only as a label and
   * its value. The card resolves `field` against the chart; the script only names
   * which fact goes where. Absent from INPUT_WIDGET_KINDS: it collects nothing.
   */
  | { kind: 'fact'; field: PacFactField; label: string }
  /**
   * A Patient field shown with its chart value and edited in place. An edit
   * writes the Patient record, not the note, so it carries no answer id and is
   * absent from INPUT_WIDGET_KINDS. The card resolves `field` and owns the write.
   */
  | { kind: 'profileField'; field: PacProfileField; label: string; say?: string }
  /**
   * A field of the patient's insurance card, read from their insurance
   * Coverage and saved straight to it. Like `profileField`, it carries no
   * answer id and is absent from INPUT_WIDGET_KINDS: the card's member ID and
   * group number must never be stored in the note, which roles that cannot
   * read Coverage can read. The card resolves `field` and owns the write.
   */
  | { kind: 'insuranceCardField'; field: PacInsuranceCardField; label: string; say?: string }
  /**
   * The policyholder of someone else's plan (BAR-2998): legal name and date of
   * birth, read from and written to the insurance Coverage's subscriber through
   * `update-insurance-coverage`, never the note. Like `insuranceCardField`, it
   * carries no answer id. The card resolves the relationship from Whose plan.
   */
  | { kind: 'policyholderField'; label: string; say?: string }
  /**
   * A dashed sub-block grouping related widgets under an optional heading.
   *
   * `when` shows the block only for some answers — the employer section asks
   * "Who's your employer?" only of a patient who has one. A hidden block's
   * answers stay on the draft but leave the summary and every action, the same
   * as a beat that doesn't apply: see `answersThatApply`.
   */
  | {
      kind: 'group';
      title?: string;
      items: PacWidget[];
      when?: (answers: PacBeatAnswers, context?: PacBeatContext) => boolean;
    };

/** The pre-consultation facts a `fact` widget can show. */
export type PacFactField = 'procedure' | 'location' | 'track' | 'bmi' | 'age';

/** The Patient fields a `profileField` widget can edit. */
export type PacProfileField = 'legalName' | 'preferredName' | 'birthDate' | 'birthSex' | 'homeAddress';

/** The insurance Coverage fields an `insuranceCardField` widget can edit. */
export type PacInsuranceCardField = 'memberId' | 'groupNumber';

/**
 * Widget kinds that accept operator input and therefore contribute an answer.
 * The intro-call and hand-off tests assert that neither script uses `text`.
 */
export const INPUT_WIDGET_KINDS = [
  'checkbox',
  'select',
  'locationSelect',
  'employerLocationSelect',
  'employerSearch',
  'verificationTask',
  'insurerSelect',
  'chips',
  'multiChips',
  'text',
  'slotGrid',
  'procedureDateGrid',
] as const;

export interface PacBeat {
  id: string;
  title: string;
  items: PacWidget[];
  /**
   * Opens with the card and sits outside the one-open-at-a-time accordion, so it
   * stays open while other beats open and close. Still closes on its own click.
   */
  pinned?: boolean;
  /**
   * Why the beat doesn't apply to this patient, or undefined when it does. A beat
   * that doesn't apply stays on the card, dimmed and closed, with this reason in
   * its summary slot ("ESG only", "Age 22+ only"): a vanishing section looks like
   * a bug, and a grayed one teaches the rule. Check the rules in order and return
   * the first that fails.
   */
  disabledReason?: (
    answers: PacBeatAnswers,
    patient: Patient | undefined,
    context?: PacBeatContext
  ) => string | undefined;
}

/**
 * What a beat's rules read besides the answers and the Patient: facts the card
 * looks up (BAR-2919). Optional throughout — a rule that needs a fact the card
 * has not read treats it as not known.
 */
export interface PacBeatContext {
  /** The patient's track, once read; undefined also when none is recorded. */
  track?: Track;
  trackLoaded?: boolean;
  /**
   * Whether insurance is offered where the patient lives, from bariendo-be's
   * coverage states — never a list kept in Orbit.
   */
  coverageArea?: CoverageAreaState;
}

export type CoverageAreaState =
  | { status: 'loading' }
  /** The lookup failed: no state rule. `state` is the address's, if it has a code. */
  | { status: 'error'; state: string | null }
  | { status: 'ready'; state: string | null; offered: boolean };

/**
 * What a beat's `disabledReason` reads from the note. Mirrors
 * `PatientAccessCallAnswers` in patient-access-note.ts, duplicated for the same
 * reason as `PacScriptVisitType` below.
 */
export interface PacBeatAnswers {
  values: Record<string, string | string[] | boolean | undefined>;
}

/** Mirrors `PacVisitType` in patient-access-note.ts. Duplicated rather than imported
 * because patient-access-note.ts already imports from this module, and importing
 * back would make the pair cyclic. `PacVisitType` is assignable to this. */
export type PacScriptVisitType = 'zoom' | 'inbound' | 'pn-consultation';

const SCHEDULED_OPENING: PacBeat = {
  id: 'open',
  title: 'Open & confirm identity',
  items: [
    {
      kind: 'checkbox',
      id: 'open_hi',
      label: 'Introduce yourself',
      say: 'Hi [first name], I’m [rep] with Everself — how are you doing today?',
    },
    {
      kind: 'checkbox',
      id: 'open_who',
      label: 'Read back name and callback number',
      say: 'I have [full name] and the number ending [last four] — is that right?',
    },
    { kind: 'checkbox', id: 'open_frame', label: 'Set the frame', say: FRAME + FRAME_CAMERA },
  ],
};

const PATHWAY: PacBeat = {
  id: 'pathway',
  title: 'Confirm the procedure',
  items: [
    {
      kind: 'select',
      id: 'procedure',
      label: 'Confirm the procedure',
      options: PROCEDURE_OPTIONS,
      say: 'I see you’re interested in [procedure]. Is that right?',
    },
    {
      kind: 'select',
      id: 'prior',
      label: 'Confirm previous bariatric surgery',
      options: PRIOR_PROCEDURE_OPTIONS,
      say: 'Just to confirm, have you had a weight loss procedure before?',
    },
    {
      kind: 'conditionalScript',
      condition: 'If the procedure is ESG or the gastric balloon —',
      text: 'And confirming you have not had a weight loss procedure before?',
    },
    {
      kind: 'checkbox',
      id: 'path_fix',
      label: 'Correct or confirm the procedure',
      say: 'Ok great, [procedure] would be the right choice for you.',
    },
    {
      kind: 'conditionalScript',
      condition: 'If they have had a weight loss surgery before —',
      text: 'In your case, this would be Everself SIS or Everself TORe, which is designed for those who have had a weight loss surgery before.',
    },
    { kind: 'warning', text: 'Never tell a revision caller they are getting a standard ESG' },
  ],
};

/** The four follow-ups under "What brought them in" that both card types share. */
const WHYNOW_FOLLOW_UPS: PacWidget = {
  kind: 'group',
  items: [
    { kind: 'checkbox', id: 'wn_tried', label: 'Understand what they’ve tried', say: 'What have you tried so far?' },
    {
      kind: 'chips',
      id: 'wn_glp',
      label: 'Confirm GLP-1 experience',
      options: GLP1_OPTIONS,
      say: 'Have you tried GLP-1 medications like Ozempic or Mounjaro before?',
    },
    { kind: 'checkbox', id: 'wn_went', label: 'Hear how it went', say: 'How did that go for you?' },
    {
      kind: 'checkbox',
      id: 'wn_diff',
      label: 'Find what needs to be different',
      say: 'What are you hoping will be different this time?',
    },
  ],
};

/** The scheduled call carries the eligibility form's goal, so it is read back rather than asked. */
const SCHEDULED_WHYNOW: PacBeat = {
  id: 'whynow',
  title: 'What brought them in',
  items: [
    { kind: 'prefilledValue', id: 'wn_main', label: 'Goal at signup' },
    {
      kind: 'script',
      text: 'When you signed up, you said you were most interested in [their goal]. Before we get into the details, tell me a bit about that?',
    },
    WHYNOW_FOLLOW_UPS,
  ],
};

/** Per-procedure copy, reshaped from PROCEDURE_EXPLANATIONS into one line each. */
export const EXPLAIN_PROC: Record<string, string> = Object.fromEntries(
  Object.entries(PROCEDURE_EXPLANATIONS).map(([procedure, copy]) => [procedure, copy.proc])
);
export const EXPLAIN_DUR: Record<string, string> = Object.fromEntries(
  Object.entries(PROCEDURE_EXPLANATIONS).map(([procedure, copy]) => [procedure, copy.dur])
);

/**
 * Everything from "Explain the procedure" onward. One array, referenced by both
 * card types, so the two cannot drift.
 */
const SHARED_TAIL: readonly PacBeat[] = [
  {
    id: 'plan',
    title: 'Explain the procedure',
    items: [
      {
        kind: 'checkbox',
        id: 'pl_proc',
        label: 'The procedure',
        sayByProcedure: EXPLAIN_PROC,
        sayFallback: 'Confirm the procedure first — the explanation names it out loud.',
      },
      {
        kind: 'checkbox',
        id: 'pl_dur',
        label: 'Duration and recovery',
        sayByProcedure: EXPLAIN_DUR,
        sayFallback: 'Confirm the procedure first.',
      },
      {
        kind: 'computed',
        field: 'weightLossEstimate',
        label: 'Weight loss expectations',
        say: 'At your starting weight, that works out to about [lb] on average.',
      },
      { kind: 'checkbox', id: 'pl_care', label: '12 months of Everself care', say: CARE },
      {
        kind: 'group',
        items: [{ kind: 'checkbox', id: 'pl_q', label: 'Check for understanding', say: 'Any questions so far?' }],
      },
    ],
  },
  {
    id: 'loc',
    title: 'Location & date they’re interested in',
    items: [
      {
        kind: 'select',
        id: 'loc',
        label: 'Potential location',
        optionsSource: 'markets',
        say: 'I can see the potential procedure dates for you. It looks like you’re closest to [location]. Is that what you have in mind?',
      },
      { kind: 'computed', field: 'physicianFlag', label: 'Physician' },
      {
        kind: 'multiChips',
        id: 'procdate',
        label: 'Procedure date',
        options: PROCEDURE_DATE_OPTIONS,
        say: 'It looks like we might have availability at this location in a few weeks. Do you have a timeline for when you might want to have this procedure?',
      },
      { kind: 'checkbox', id: 'doc_bio', label: 'Doctor bio', say: 'This would be with Dr. [name].' },
      { kind: 'computed', field: 'doctorBio', label: 'Doctor bio' },
    ],
  },
  {
    id: 'price',
    title: 'Program & price',
    items: [
      { kind: 'checkbox', id: 'pr_price', label: 'Pricing', say: PRICING },
      { kind: 'select', id: 'pay', label: 'Payment option', options: PAYMENT_OPTIONS, say: PAYMENT },
      {
        kind: 'group',
        title: 'Cherry pre-approval',
        items: [
          { kind: 'computed', field: 'cherryStatus', label: 'Cherry status' },
          {
            kind: 'actionLink',
            label: 'Send the application',
            linkText: 'Send Cherry link',
            action: 'sendCherryLink',
            say: 'I’ll send you the Cherry application now — it will show your actual options.',
          },
        ],
      },
      {
        kind: 'group',
        title: 'Employer benefits',
        items: [
          {
            kind: 'actionLink',
            label: 'Check their employer',
            linkText: 'everself.com/coverage',
            action: 'checkEmployerCoverage',
          },
          {
            kind: 'conditionalScript',
            condition: 'If covered —',
            text: 'It looks like your employer may include Everself ESG in their benefits. It usually takes us a few days to confirm. In the meantime, we can get you in with the consultation for now.',
          },
          {
            kind: 'conditionalScript',
            condition: 'If not —',
            text: 'It looks like your employer isn’t one of our partners. We can try financing or HSA/FSA.',
          },
        ],
      },
    ],
  },
  {
    id: 'clinical',
    title: 'Offer the consultation',
    items: [{ kind: 'checkbox', id: 'c_consult', label: 'Confirm interest in consultation', say: CONSULT }],
  },
  {
    id: 'book',
    title: 'Book the consultation',
    items: [
      {
        kind: 'checkbox',
        id: 'book_ok',
        label: 'Confirm availability',
        say: 'It looks like we have an opening with one of our providers today at [time]—just a couple of hours from now. Would that work for you?',
      },
      { kind: 'slotGrid', id: 'slot' },
      { kind: 'checkbox', id: 'book_expect', label: 'Share what to expect from the call', say: EXPECT },
    ],
  },
  {
    id: 'close',
    title: 'Close',
    items: [
      {
        kind: 'select',
        id: 'close',
        label: 'Outcome',
        options: OUTCOME_OPTIONS,
        say: 'You’re all set for [date and time] with [provider name]. We’ll send the Zoom link and reminders, and the confirmation includes a rescheduling link. Is there anything else I can help with before we wrap up?',
      },
      {
        kind: 'checkbox',
        id: 'close_thanks',
        label: 'Thank you',
        say: 'It was such a pleasure to meet you [name]. Have a great day.',
      },
      {
        kind: 'group',
        title: 'Internal — not said on the call',
        items: [
          { kind: 'select', id: 'likelihood', label: 'Likelihood of becoming a patient', options: LIKELIHOOD_OPTIONS },
          { kind: 'multiChips', id: 'barrier', label: 'Potential barrier', options: BARRIER_OPTIONS },
        ],
      },
    ],
  },
];

/**
 * The **scheduled** intro-call script — the 15-minute qualifying call a booked
 * `ci-intro-call` appointment opens.
 *
 * Beat order comes from design-system #17:
 *   open → confirm the procedure → what brought them in → explain the procedure
 *   → location and date → program and price → offer the consultation
 *   → book the consultation → close
 *
 * Legacy. Intake 1.1 replaced the scheduled call with the PN consultation
 * (`pn-consultation-script.ts`) and BAR-2612 rebuilt the inbound call as its own
 * four-section script (`pac-intro-call-script.ts`). What is left here runs only
 * for `ci-intro-call` appointments booked before that split and for the notes
 * they wrote, which is why it is frozen rather than maintained: change the copy
 * on one of the two live scripts instead.
 */
export function buildCiIntroCallScript(): readonly PacBeat[] {
  return [SCHEDULED_OPENING, PATHWAY, SCHEDULED_WHYNOW, ...SHARED_TAIL];
}

/** Every beat the scheduled card shows, in order — what `summarizeBeat` is keyed against. */
export const PAC_INTRO_CALL_BEAT_IDS = buildCiIntroCallScript().map((beat) => beat.id);

/**
 * The fields the eligibility form pre-fills. An inbound call has no form, so
 * these simply start empty and the rep picks them from selects already on the
 * card.
 *
 * `loc` joined the list in BAR-2597: the form resolves a market from the lead's
 * ZIP, and until the market vocabularies were reconciled that value could not be
 * offered to the card without naming a market the rep could not select.
 */
export const PAC_PREFILL_FIELD_IDS = ['procedure', 'wn_main', 'prior', 'loc'] as const;

/** Depth-first walk of a beat's widgets, descending into groups. */
export function flattenWidgets(items: readonly PacWidget[]): PacWidget[] {
  return items.flatMap((item) => (item.kind === 'group' ? flattenWidgets(item.items) : [item]));
}

/**
 * Which line a widget speaks, given the procedure recorded on the note.
 *
 * One place decides this, so the renderer never reaches into `sayByProcedure`
 * itself and the fallback behaviour is testable without React. A procedure with
 * no line falls back rather than going silent: an empty beat reads as "nothing
 * to say here", which is worse than a prompt to confirm the procedure.
 */
export function resolveWidgetSay(widget: PacWidget, procedure: string | undefined): string | undefined {
  if ((widget.kind === 'checkbox' || widget.kind === 'action') && widget.sayByProcedure) {
    const line = procedure ? widget.sayByProcedure[procedure] : undefined;
    return line ?? widget.sayFallback;
  }
  return 'say' in widget ? widget.say : undefined;
}
